import React, { useEffect, useState } from 'react';
import { Button, Tooltip } from 'antd';
import { AccountInfo } from '@/components/AccountInfo/AccountInfo';
import { MessageIcon } from '@/components/MessageIcon/MessageIcon';
import { IEntity } from '@/shared/interfaces/entity.interface';
import { COMMON, MAIN } from '@/configs/main.config';
import declineIcon from '@/public/icons/decline.svg';
import { getRegionByPhoneNumber } from '@/services/phoneRegionLocator/getRegionByPhoneNumber';
import { State } from '@/services/state.service';
import { useStore } from '@/stores';
import { observer } from 'mobx-react-lite';
import { ISession, SipJs } from '@/services/sip-js.service';
import { ESessionStatus } from '@/shared/enums/ringing-status.enum';
import { USER_TYPE } from '@/shared/constants/user';
import './Outbound.scss';

export const Outbound = observer(() => {
  const {
    callStore: { contacts, setWrapUpSession },
  } = useStore();
  const [user, setUser] = useState<IEntity>(COMMON.UNKNOWN_ENTITY);
  const [userRegion, setUserRegion] = useState('Unknown');

  const declineCall = () => {
    const idleSession = SipJs.getSessionByType('idle');
    SipJs.declineCall(idleSession.id);
  };

  const setupWrapUp = (idleSession: ISession) => {
    const directWrapUp: number = Number(
      localStorage.getItem(MAIN.lsWrapUpToggleAllCallsKey),
    );

    if (directWrapUp > 0) {
      State.setState({
        wrapUpQueue: {
          isWorking: false,
          time: directWrapUp,
        },
      });
    }

    setWrapUpSession(idleSession);
  };

  useEffect(() => {
    if (
      user?.initialPhone &&
      user?.initialPhone !== COMMON.UNKNOWN_ENTITY.phone
    ) {
      setUserRegion(getRegionByPhoneNumber(user.initialPhone));
    }
  }, [user]);

  useEffect(() => {
    if (contacts) {
      const { activeEntities } = State.state$.getValue();
      const idleSession: ISession = SipJs.getSessionByType('idle');

      if (idleSession.remoteUser?.type === USER_TYPE.INTERNAL) {
        setUser(activeEntities[0]);
      } else {
        const user: IEntity = contacts.length
          ? contacts[0]
          : COMMON.UNKNOWN_ENTITY;
        idleSession.setRemoteUser(USER_TYPE.EXTERNAL, contacts);
        setUser(user);
      }

      if (!idleSession?.sessionState$) return;

      const sub$ = idleSession.sessionState$.subscribe(
        (status: ESessionStatus) => {
          if (status === ESessionStatus.ACCEPTED) {
            setupWrapUp(idleSession);
          }
        },
      );

      return () => sub$.unsubscribe();
    }
  }, [contacts]);

  useEffect(() => {
    const keyListenerOutbound = (e: KeyboardEvent) => {
      if (e?.key === 'Escape') {
        declineCall();
      }
    };
    document.addEventListener('keydown', keyListenerOutbound);
    return () => {
      document.removeEventListener('keydown', keyListenerOutbound);
    };
  }, []);

  const idleSession: ISession = SipJs.getSessionByType('idle');

  return (
    <div className="outbound">
      <div className="main">
        <div className="call-info">
          <div className="main-text">Calling...</div>
          {idleSession?.remoteUser?.type === USER_TYPE.EXTERNAL && (
            <div className="back-text">{userRegion}</div>
          )}
        </div>
        <AccountInfo
          entity={user?.type}
          id={user?.id}
          fullName={user?.fullName}
          phone={user?.initialPhone}
          searchIsActive={!contacts}
        />
      </div>
      <Tooltip title="Decline">
        <Button
          danger
          data-testid="outbound-decline-call"
          className="call-button"
          type="primary"
          shape="circle"
          onClick={declineCall}
          icon={<MessageIcon svgUrl={declineIcon} />}
        />
      </Tooltip>
    </div>
  );
});
